// Where the keyboard is on the rail, in terms that survive the list being
// rebuilt (SPEC-redesign §8, D-f).
//
// Its own module because `src/axis/axis-view.ts` is ratcheted at 401 lines and
// the ratchet only ever goes down (ADR-0015): „a file that needs more room
// needs a split". Nothing about it changed in the move.

/** The controls a row can hand focus to, in the order they are tried back. */
const FOCUSABLE = '.row__title, .row__voice, .run, .detail__close, .detail__action';

type Focused = { id: string | undefined; kind: string; index: number };

/**
 * Where the keyboard is, in terms that survive the list being rebuilt.
 *
 * The kreska's own slot is never one of those places. It is the one child of
 * the list that a rebuild does not touch, so focus inside it is never lost —
 * and it carries no `data-id`, so the restore below would take a resident who
 * was on „Linie 16, 174…" and drop them on whatever row now stands at that
 * index. Saving nothing is what leaves that focus exactly where it is.
 */
export function focusedNow(list: HTMLElement, kreska: HTMLElement): Focused | null {
  const active = document.activeElement;
  if (!(active instanceof HTMLElement) || !list.contains(active)) return null;
  if (kreska.contains(active)) return null;
  const row = active.closest<HTMLElement>('li');
  const kind = active.className.split(' ')[0] ?? '';
  return { id: row?.dataset.id, kind, index: row ? [...list.children].indexOf(row) : -1 };
}

/**
 * Puts the keyboard back where it was, and this is harder than finding the same
 * element again — very often that element is gone *because* of what the
 * resident just did. Closing a card removes the `‹ Zamknij szczegóły` they
 * pressed; muting a voice collapses the whole row into a thin line. Matching on
 * the control alone dropped them on `<body>`, at the top of the document, with
 * the entire rail to walk again (§8).
 *
 * So: the same control if it is still there, else that row's own title, else
 * whatever now stands at that place in the list. Restoring a voice is the one
 * case with a better answer than "the first control there": the run is replaced
 * by the rows it was hiding, and the control that matches what was just pressed
 * is the first of those rows' own tag, not its title.
 */
export function restoreFocus(list: HTMLElement, was: Focused | null): void {
  if (!was) return;
  const row = was.id ? list.querySelector<HTMLElement>(`[data-id="${CSS.escape(was.id)}"]`) : null;
  const inRow =
    row && (row.querySelector<HTMLElement>(`.${was.kind}`) ?? row.querySelector<HTMLElement>('.row__title'));
  // `preventScroll`: putting the keyboard back is not a reason to move the rail
  // under the reader's eyes; the scroll has one owner and it is below.
  if (inRow) return inRow.focus({ preventScroll: true });

  const prefer = was.kind === 'run' ? '.row__voice' : null;
  for (let i = Math.min(was.index, list.children.length - 1); i >= 0; i--) {
    const at = list.children[i];
    const here =
      (prefer ? at?.querySelector<HTMLElement>(prefer) : null) ?? at?.querySelector<HTMLElement>(FOCUSABLE);
    if (here) return here.focus({ preventScroll: true });
  }
}
